/**
 * hb-derived-routes — the SCHEMA-FREE, behaviour-free ROUTE-ONLY half of the
 * home-battery derived tail (#904, the #705 route-only precedent extended to the
 * hand-authored DERIVED plugins).
 *
 * The FULL `hbDerivedPlugin` (`./hb-derived-plugin.js`) composes its behavioural
 * entities (`store` + three `query`s) ALONGSIDE its two `component`/`route` pairs.
 * The studio ENTRY's router needs only the ROUTE TABLE + each route's (lazy)
 * editor, never a `store`/`query` entity — so composing the full plugin on the
 * eager entry drags those behavioural entity literals onto the entry for no boot
 * benefit. This module carries the route + component entities ONLY, so the eager
 * `genome.ts` composes THIS while `genome-full.ts` keeps the FULL plugin (whose
 * full 16-kind graph the off-entry agent catalog / PERCEIVE / genome-graph /
 * census consume).
 *
 * The lazy editor consts + the `component`/`route` entity literals + the plugin
 * id + the route path consts live HERE (the entry-safe home);
 * `hb-derived-plugin.ts` IMPORTS {@link HB_DERIVED_ROUTE_ENTITIES} +
 * {@link HB_DERIVED_EDITORS} + the id + paths and re-exports them, then prepends
 * its behavioural block — so `HB_DERIVED_ENTITIES` stays ELEMENT-IDENTICAL to
 * before. Nothing here reaches the FULL plugin, so `genome.ts` → this module never
 * pulls the behavioural entities onto the entry.
 *
 * @module @ubiclouds/v5-studio/home-battery/hb-derived-routes
 */

import { lazy, type ComponentType } from 'react';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { EntityDef } from '@ubiclouds/v5-sdk/genome';

/** The bespoke derived-tail plugin id (its entity namespace). */
export const HB_DERIVED_PLUGIN_ID = 'hb-derived';

/** The configurator route path (the bespoke profiler view). */
export const HB_CONFIGURATOR_PATH = '/home-battery/configurator';

/** The guided wizard route path (#430 — property → supply → EV? → tariff → recommendation). */
export const HB_WIZARD_PATH = '/home-battery/wizard';

/** The split-chunk configurator editor (derive module + view stay off the entry). */
const LazyHbConfigurator: ComponentType = lazy(() => import('./HbConfigurator.js'));

/** The split-chunk wizard editor (#430 — the guided flow stays off the entry too). */
const LazyHbWizard: ComponentType = lazy(() => import('./HbWizard.js'));

/**
 * The `component`/`route` entities the derived tail contributes (keyed by
 * `meta.id`). Element-identical to the trailing route block of the historic
 * `HB_DERIVED_ENTITIES` — the FULL plugin re-uses this array verbatim.
 */
export const HB_DERIVED_ROUTE_ENTITIES: readonly EntityDef[] = [
  { kind: 'component', meta: { id: `${HB_DERIVED_PLUGIN_ID}:component/hb-configurator` } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: `${HB_DERIVED_PLUGIN_ID}:component/hb-configurator` }],
    meta: {
      id: `${HB_DERIVED_PLUGIN_ID}:route/configurator`,
      path: HB_CONFIGURATOR_PATH,
      title: 'Home-Battery Configurator',
    },
  },
  // ── #430 — the guided wizard flow (a second component/route pair in the same
  //    hand-authored NON-CRUD tail; gate-4's pinned count self-adjusts) ──
  { kind: 'component', meta: { id: `${HB_DERIVED_PLUGIN_ID}:component/hb-wizard` } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: `${HB_DERIVED_PLUGIN_ID}:component/hb-wizard` }],
    meta: {
      id: `${HB_DERIVED_PLUGIN_ID}:route/wizard`,
      path: HB_WIZARD_PATH,
      title: 'Home-Battery Wizard',
    },
  },
];

/** The lazy editor map — SHARED by the route-only plugin and the FULL plugin. */
export const HB_DERIVED_EDITORS: Readonly<Record<string, ComponentType>> = Object.freeze({
  'hb-configurator': LazyHbConfigurator,
  'hb-wizard': LazyHbWizard,
});

/**
 * The ROUTE-ONLY home-battery derived-tail plugin (composed on the studio ENTRY by
 * `genome.ts`). Same `editors` as the FULL plugin; `entities()` are the two
 * `component`/`route` pairs ONLY — the behavioural entities live in the FULL
 * plugin, composed OFF the entry by `genome-full.ts`.
 */
export const hbDerivedRoutePlugin: ComposableGenomePlugin = Object.freeze({
  id: HB_DERIVED_PLUGIN_ID,
  name: 'Home-Battery Configurator',
  version: '1.0.0',
  trustTier: 'core' as const,
  editors: HB_DERIVED_EDITORS,
  entities(): EntityDef[] {
    return [...HB_DERIVED_ROUTE_ENTITIES];
  },
});
